import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatCurrency } from '@/lib/utils'

export interface BulkAdjustTarget {
  id: string
  product_name?: string
  current_price: number
}

interface BulkAdjustPricesModalProps {
  open: boolean
  targets: BulkAdjustTarget[]
  onOpenChange: (open: boolean) => void
  onApply: (next: Array<{ id: string; unit_price: number }>) => void
}

type AdjustMode = 'percent' | 'flat'

export function BulkAdjustPricesModal({
  open,
  targets,
  onOpenChange,
  onApply,
}: BulkAdjustPricesModalProps) {
  const [mode, setMode] = useState<AdjustMode>('percent')
  const [amount, setAmount] = useState('')

  const computed = useMemo(() => {
    const parsed = parseFloat(amount)
    if (isNaN(parsed)) return null
    return targets.map((t) => {
      const next =
        mode === 'percent'
          ? round2(t.current_price * (1 + parsed / 100))
          : round2(t.current_price + parsed)
      return { ...t, next_price: Math.max(0, next) }
    })
  }, [mode, amount, targets])

  const invalidAmount = amount.trim() === '' || isNaN(parseFloat(amount))

  const handleApply = () => {
    if (!computed) return
    onApply(computed.map((c) => ({ id: c.id, unit_price: c.next_price })))
    setAmount('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl max-h-[85vh] overflow-y-auto"
        aria-describedby="bulk-adjust-desc"
      >
        <DialogHeader>
          <DialogTitle>Bulk adjust prices</DialogTitle>
          <DialogDescription id="bulk-adjust-desc">
            Applies to {targets.length} selected {targets.length === 1 ? 'entry' : 'entries'}.
            Changes are staged in the editor — you still need to click Save to commit them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium mb-1">Adjustment type</legend>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="adjust-mode"
                  value="percent"
                  checked={mode === 'percent'}
                  onChange={() => setMode('percent')}
                  className="h-4 w-4"
                />
                <span className="text-sm">Percentage (+/−)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="adjust-mode"
                  value="flat"
                  checked={mode === 'flat'}
                  onChange={() => setMode('flat')}
                  className="h-4 w-4"
                />
                <span className="text-sm">Flat amount (+/−)</span>
              </label>
            </div>
          </fieldset>

          <div>
            <Label htmlFor="adjust-amount">
              {mode === 'percent'
                ? 'Percentage change (e.g., 10 for +10%, -5 for −5%)'
                : 'Amount to add (negative to reduce)'}
            </Label>
            <Input
              id="adjust-amount"
              type="number"
              step="any"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={mode === 'percent' ? '10' : '5.00'}
              aria-describedby="adjust-help"
            />
            <p id="adjust-help" className="text-xs text-muted-foreground mt-1">
              Resulting prices are rounded to 2 decimal places and never go below $0.
            </p>
          </div>

          {computed && computed.length > 0 && (
            <div className="border rounded-md max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Product</th>
                    <th className="text-right p-2 font-medium">Current</th>
                    <th className="text-right p-2 font-medium">New</th>
                    <th className="text-right p-2 font-medium">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {computed.map((c) => {
                    const delta = c.next_price - c.current_price
                    return (
                      <tr key={c.id} className="border-t">
                        <td className="p-2">{c.product_name || c.id}</td>
                        <td className="p-2 text-right">{formatCurrency(c.current_price)}</td>
                        <td className="p-2 text-right font-medium">
                          {formatCurrency(c.next_price)}
                        </td>
                        <td
                          className={`p-2 text-right ${delta > 0 ? 'text-success' : delta < 0 ? 'text-destructive' : ''}`}
                        >
                          {delta > 0 ? '+' : ''}
                          {formatCurrency(delta)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleApply} disabled={invalidAmount}>
              Apply to {targets.length} {targets.length === 1 ? 'entry' : 'entries'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function round2(n: number) {
  return Math.round((n + Number.EPSILON) * 100) / 100
}
